<template>
  <div
    class="MobileMenuWrapper"
    :class="{ active: isOpen }"
    :aria-hidden="!isOpen"
    :inert="!isOpen"
  >
    <div class="main-nav-wrapper">
      <ul
        class="flex flex-column flex-nowrap flex-gap-40"
        role="list"
      >
        <UIHeaderNavItem
          v-for="nav in totalNav"
          :key="nav.id"
          :navigation="nav"
          :style="'mobile'"
          :add-top-level-child="true"
        />
      </ul>
    </div>
    <div class="bottom-menu">
      <button
        type="button"
        data-accessibility-menu-toggle="true"
        class="mobile-accessibility-toggle flex align-center justify-center flex-gap-16"
        :aria-label="accessibilityIsExpanded ? 'Sluit toegankelijkheidsmenu' : 'Open toegankelijkheidsmenu'"
        :aria-expanded="accessibilityIsExpanded"
        :aria-controls="accessibilityMenuId"
        @click="accessibilityIsExpanded = !accessibilityIsExpanded"
      >
        <UIIconComponent
          icon="accessibility"
          class="icon"
          aria-hidden="true"
        />
        <span>Toegankelijkheid</span>
      </button>
      <UIHeaderSecondary :style="'mobile'" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { Navigation } from '@tc8l/nuxt-base'

  const props = defineProps<{
    isOpen: boolean
  }>()

  const localization = useBaseLocalization()
  const currentLanguage = computed(() => localization.getCurrentLanguage())
  const isMultiLanguage = ref(localization.isMultiLanguage())

  const homeUrl = computed(() => {
    if (!isMultiLanguage.value) {
      return '/'
    }

    if (currentLanguage.value.name == localization.getDefaultLanguage().name) {
      return '/'
    }

    return `/${currentLanguage.value.name}`
  })

  const accessibilityIsExpanded = useState<boolean>('accessibility-menu-expanded', () => false)
  const accessibilityMenuId = 'accessibility-menu-panel'

  const mainNav = ref<Navigation[]>(await baseGetNavigationByGroup(10))
  const hamburgerNav = ref<Navigation[]>(await baseGetNavigationByGroup(40))

  const totalNav = ref([...mainNav.value, ...hamburgerNav.value])

  let stopMobileIsExpandedWatch: (() => void) | null = null

  onMounted(() => {
    stopMobileIsExpandedWatch = watch(
      () => props.isOpen,
      (expanded) => {
        if (typeof window === 'undefined') return
        if (expanded) {
          document.documentElement.classList.add('overlay-open')
        } else {
          document.documentElement.classList.remove('overlay-open')
        }
      },
    )
  })

  onUnmounted(() => {
    if (stopMobileIsExpandedWatch) stopMobileIsExpandedWatch()
  })
</script>

<style lang="scss">
  .MobileMenuWrapper {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 7.5rem;
    left: 0;
    width: 100%;
    height: calc(100dvh - 7.5rem);
    background-color: $beige;
    z-index: -1;
    color: $color-text;
    transition: 0.6s all var(--ease-fast-in-slow-out);
    visibility: hidden;
    transform: translateY(-100%);
    border-radius: 4rem;

    .home-logo {
      width: 14rem;
      height: auto;
      display: block;

      img {
        width: 100%;
        height: auto;
      }
    }

    .main-nav-wrapper {
      padding: 20vh 2.4rem 0 2.4rem;
      overflow-y: scroll;
      padding-bottom: 10rem;
    }

    &.active {
      opacity: 1;
      border-radius: 0;
      visibility: visible;
      transform: translateX(0) translateY(0) scale(1);
    }

    .top-wrapper {
      padding: 2.4rem;
    }

    .bottom-menu {
      position: absolute;
      bottom: 0;
      width: 100%;
      padding: 2.4rem;
      left: 0;
      z-index: 10;
    }

    .mobile-accessibility-toggle {
      width: 100%;
      margin-bottom: 1.6rem;
      padding: 0.8rem 0;
      background: none;
      border: none;
      font-size: 1.6rem;
      font-weight: 500;
      color: $color-text;
      cursor: pointer;

      .icon {
        font-size: 2.4rem;
      }
    }
  }
</style>
